import CreatableSelect from "react-select/creatable";
import * as ReactSelect from "react-select";
import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

export type OptionType = {
  label: string;
  value: string;
  data?: any;
  __isNew__?: boolean;
};

export type Props = {
  name: string;
  className?: string;
  options: OptionType[];
  disabled?: boolean;
  isMulti?: boolean;
  isCreatable?: boolean;
  placeholder?: string;
  noOptionsMessage?: string;
  isLoading?: boolean;
  isWithIcon?: boolean;
  value?: OptionType | OptionType[] | null;
  onSelect?: (val: OptionType | OptionType[] | null) => void;
  onCreate?: (input: string) => void;
  onBlur?: () => void;
};

export const SelectBase = forwardRef<any, Props>(
  (
    {
      name,
      options,
      isMulti = false,
      isCreatable = false,
      isLoading = false,
      disabled = false,
      placeholder,
      isWithIcon = false,
      className,
      value,
      onSelect,
      onCreate,
      onBlur,
    },
    ref
  ) => {
    const SelectComponent = isCreatable ? CreatableSelect : ReactSelect.default;

    const handleChange = (selected: any) => {
      onSelect?.(selected ?? null);
    };

    const handleCreate = (input: string) => {
      onCreate?.(input);

      const newOption = {
        label: input,
        value: input.toLowerCase().replace(/\s+/g, "-"),
      };

      if (isMulti) {
        const current = Array.isArray(value) ? value : [];
        onSelect?.([...current, newOption]);
      } else {
        onSelect?.(newOption);
      }
    };

    return (
      <div className={twMerge("border-none shadow-none", className)}>
        <SelectComponent
          ref={ref}
          data-testid={`select-${name}`}
          unstyled
          inputId={name}
          isDisabled={disabled}
          isLoading={isLoading}
          isMulti={isMulti}
          options={options}
          value={value ?? null}
          onChange={handleChange}
          onCreateOption={isCreatable ? handleCreate : undefined}
          placeholder={placeholder}
          menuPortalTarget={document.body}
          onBlur={onBlur}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
          classNames={{
            container: ({ isDisabled }) =>
              twMerge(
                "transition-all duration-200 relative w-full drop-shadow-lg",
                isDisabled &&
                  "rounded-sm bg-brand-300 cursor-not-allowed drop-shadow-none"
              ),
            control: ({ isDisabled, isFocused }) =>
              twMerge(
                "transition-all duration-200 w-full border flex h-10 w-full rounded-sm border-brand-800 bg-transparent px-3 py-2 text-sm shadow-sm transition-colors",
                isFocused && "border-2 border-brand-600",
                isDisabled && "border-brand-300"
              ),
            input: () => (isWithIcon ? "pl-6" : ""),
            menu: () =>
              "relative flex flex-col border-2 border-brand-600 rounded-sm mt-2 mb-8 bg-brand-50 overflow-y-auto max-h-[300px]",
            option: ({ isFocused, isSelected, data }) =>
              twMerge(
                "transition-all duration-200 px-4 py-2 w-full",
                isFocused && "bg-brand-100",
                isSelected && "bg-brand-100",
                data?.__isNew__ &&
                  "text-brand-600 font-medium before:content-['+'] before:mr-2"
              ),
            noOptionsMessage: () =>
              "flex px-4 py-2 w-full text-left text-brand-400 text-sm",
            placeholder: () =>
              twMerge(
                "transition-all duration-200 text-brand-400",
                isWithIcon && "pl-6"
              ),
            singleValue: () => twMerge(isWithIcon && "pl-6"),
            loadingMessage: () =>
              "px-4 py-2 w-full text-left text-brand-400 text-sm",
            valueContainer: () => "flex flex-1 items-center gap-2",
            indicatorsContainer: ({ isDisabled }) =>
              twMerge(
                "transition-all duration-200 flex items-center gap-1",
                isDisabled && "text-brand-400"
              ),
            indicatorSeparator: () => "hidden",
          }}
        />
      </div>
    );
  }
);

export default SelectBase;
